import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Package, Truck, CheckCircle2, Clock, XCircle } from "lucide-react";
import { api, fileUrl } from "@/lib/api";
import { fmtINR, fmtUSD } from "@/lib/money";
import { useAuth } from "@/context/AuthContext";

const STATUS_ICONS = {
  pending: Clock,
  paid: CheckCircle2,
  shipped: Truck,
  delivered: Package,
  cancelled: XCircle,
};
const STATUS_COLORS = {
  pending: "bg-[#FFB300] text-[#2C1810]",
  paid: "bg-[#E65C00] text-white",
  shipped: "bg-[#4A148C] text-[#FFCF52]",
  delivered: "bg-[#0F7B3B] text-white",
  cancelled: "bg-gray-500 text-white",
};

export default function Orders() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const { user, ready } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (ready && !user) { navigate("/login?next=/orders"); return; }
    if (user) {
      (async () => {
        try {
          const { data } = await api.get("/orders");
          setOrders(data);
        } catch {} finally { setLoading(false); }
      })();
    }
  }, [ready, user, navigate]);

  if (loading) return <div className="max-w-7xl mx-auto px-6 py-20 font-display text-xl text-[#4A148C]">Loading orders…</div>;

  return (
    <div className="max-w-5xl mx-auto px-6 lg:px-10 py-12">
      <h1 className="font-display text-5xl text-[#2C1810] mb-10">My Orders</h1>
      {orders.length === 0 ? (
        <div className="p-14 bg-white/60 border border-dashed border-[#FFD1A6] rounded-3xl text-center">
          <Package className="w-12 h-12 mx-auto text-[#E65C00] mb-4" />
          <div className="font-display text-2xl text-[#2C1810]">No orders yet</div>
          <p className="font-body text-[#5B3D2A] mt-2">Your festive orders will appear here.</p>
        </div>
      ) : (
        <div className="space-y-5">
          {orders.map((o) => {
            const Icon = STATUS_ICONS[o.status] || Clock;
            return (
              <div key={o.id} data-testid={`order-row-${o.id}`} className="bg-white border border-[#FFD1A6] rounded-2xl p-6 shadow-warm">
                <div className="flex flex-wrap items-center justify-between gap-4 mb-4">
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-[#4A148C] font-body">Order</div>
                    <div className="font-body text-sm text-[#2C1810]">#{o.id.slice(0, 8)}</div>
                    <div className="text-xs font-body text-[#5B3D2A]">{new Date(o.created_at).toLocaleString()}</div>
                  </div>
                  <div className={`inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-xs font-semibold ${STATUS_COLORS[o.status] || STATUS_COLORS.pending}`}>
                    <Icon className="w-3.5 h-3.5" />
                    {o.status.toUpperCase()}
                  </div>
                  <div className="text-right">
                    <div className="font-display text-2xl text-[#E65C00] font-semibold">₹{fmtINR(o.total)}</div>
                    <div className="text-[10px] font-body text-[#5B3D2A]/70">≈ ${fmtUSD(o.total)} USD</div>
                  </div>
                </div>
                <div className="flex flex-wrap gap-3">
                  {o.items.map((it, i) => (
                    <div key={i} className="flex items-center gap-3 bg-[#FFF3E0] rounded-xl p-2 pr-4">
                      <div className="w-12 h-12 rounded-lg overflow-hidden bg-white">
                        <img src={it.image ? fileUrl(it.image) : "https://images.unsplash.com/photo-1605292356183-a77d0a9c9d1d?w=200&q=80"} alt="" className="w-full h-full object-cover" />
                      </div>
                      <div className="text-xs font-body">
                        <div className="text-[#2C1810] font-semibold">{it.name}</div>
                        <div className="text-[#5B3D2A]">× {it.quantity}</div>
                      </div>
                    </div>
                  ))}
                </div>
                {o.shipping_address && (
                  <div className="mt-4 text-xs font-body text-[#5B3D2A]">
                    Ship to: {o.shipping_address.name}, {o.shipping_address.line1}, {o.shipping_address.city}, {o.shipping_address.country}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
